/**
 * Auto-save service for forum replies
 * Handles draft storage, retrieval, and cleanup
 */
export class ForumReplyAutoSave {
  constructor() {
    this.saveTimeouts = new Map();
    this.saveDelay = 500; // ms
    this.maxDraftAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  }

  /**
   * Schedules an auto-save operation
   * @param {string} questionId - The question ID
   * @param {string} content - The content to save
   * @param {Function} callback - Callback function (error) => void
   */
  scheduleAutoSave(questionId, content, callback) {
    // Clear existing timeout
    if (this.saveTimeouts.has(questionId)) {
      clearTimeout(this.saveTimeouts.get(questionId));
    }

    // Don't save empty content
    if (!content || !content.trim()) {
      this.clearDraft(questionId);
      if (callback) callback(null);
      return;
    }

    // Schedule new save
    const timeout = setTimeout(async () => {
      try {
        await this.saveDraft(questionId, content);
        if (callback) callback(null);
      } catch (error) {
        console.error('Auto-save failed:', error);
        if (callback) callback(error);
      }
      this.saveTimeouts.delete(questionId);
    }, this.saveDelay);

    this.saveTimeouts.set(questionId, timeout);
  }

  /**
   * Immediately saves a draft
   * @param {string} questionId - The question ID
   * @param {string} content - The content to save
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async saveDraft(questionId, content) {
    const draftKey = `forum-reply-draft-${questionId}`;
    const draftData = {
      content,
      timestamp: Date.now(),
      questionId,
      version: 1
    };

    try {
      localStorage.setItem(draftKey, JSON.stringify(draftData));
      return { success: true };
    } catch (error) {
      console.error('Failed to save draft:', error);
      
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError') {
        this.cleanupOldDrafts();
        // Try again after cleanup
        try {
          localStorage.setItem(draftKey, JSON.stringify(draftData));
          return { success: true };
        } catch (retryError) {
          return { success: false, error: 'Storage quota exceeded' };
        }
      }
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Loads a draft for a specific question
   * @param {string} questionId - The question ID
   * @returns {Object|null} Draft data or null if not found
   */
  loadDraft(questionId) {
    const draftKey = `forum-reply-draft-${questionId}`;
    try {
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        
        // Check if draft is not too old
        const age = Date.now() - draft.timestamp;
        if (age > this.maxDraftAge) {
          this.clearDraft(questionId);
          return null;
        }
        
        // Validate draft structure
        if (draft.questionId === questionId && draft.content) {
          return draft;
        }
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
      // Clear corrupted draft
      this.clearDraft(questionId);
    }
    return null;
  }

  /**
   * Clears a draft for a specific question
   * @param {string} questionId - The question ID
   */
  clearDraft(questionId) {
    const draftKey = `forum-reply-draft-${questionId}`;
    try {
      localStorage.removeItem(draftKey);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }

    // Clear any pending timeout
    if (this.saveTimeouts.has(questionId)) {
      clearTimeout(this.saveTimeouts.get(questionId));
      this.saveTimeouts.delete(questionId);
    }
  }

  /**
   * Gets all draft keys for cleanup purposes
   * @returns {string[]} Array of draft keys
   */
  getAllDraftKeys() {
    const draftKeys = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('forum-reply-draft-')) {
          draftKeys.push(key);
        }
      }
    } catch (error) {
      console.error('Failed to get draft keys:', error);
    }
    return draftKeys;
  }

  /**
   * Cleans up old drafts to free storage space
   */
  cleanupOldDrafts() {
    const draftKeys = this.getAllDraftKeys();
    const now = Date.now();
    
    draftKeys.forEach(key => {
      try {
        const draftData = localStorage.getItem(key);
        if (draftData) {
          const draft = JSON.parse(draftData);
          const age = now - draft.timestamp;
          
          // Remove drafts older than maxDraftAge
          if (age > this.maxDraftAge) {
            localStorage.removeItem(key);
          }
        }
      } catch (error) {
        // Remove corrupted drafts
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Gets the count of saved drafts
   * @returns {number} Number of saved drafts
   */
  getDraftCount() {
    return this.getAllDraftKeys().length;
  }

  /**
   * Checks if there are any unsaved changes for a question
   * @param {string} questionId - The question ID
   * @param {string} currentContent - Current content to compare
   * @returns {boolean} True if there are unsaved changes
   */
  hasUnsavedChanges(questionId, currentContent) {
    const draft = this.loadDraft(questionId);
    if (!draft) return false;
    
    // Compare content (normalize whitespace)
    const draftContent = draft.content.trim();
    const current = (currentContent || '').trim();
    
    return draftContent !== current && draftContent.length > 0;
  }

  /**
   * Cleanup method to call when component unmounts
   */
  cleanup() {
    // Clear all pending timeouts
    for (const timeout of this.saveTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.saveTimeouts.clear();
  }

  /**
   * Force save all pending drafts (useful before page unload)
   * @returns {Promise<void>}
   */
  async flushPendingSaves() {
    const pendingQuestions = Array.from(this.saveTimeouts.keys());
    
    // Clear timeouts and save immediately
    for (const questionId of pendingQuestions) {
      const timeout = this.saveTimeouts.get(questionId);
      clearTimeout(timeout);
      this.saveTimeouts.delete(questionId);
    }
  }
}

// Export singleton instance
export const forumReplyAutoSave = new ForumReplyAutoSave();

// Cleanup old drafts on module load
forumReplyAutoSave.cleanupOldDrafts();